import React, { useState, useEffect } from 'react';
import { advanceAppointment, rescheduleAppointment, updateAppointmentFields, getRequestableClients, getAgendaSdrs, requestAppointmentDeletion, getCallRecordingUrl, getProspectCallThread, contractTypeOf, getEvaluationByCallId, setClientNote, type DayRdv, type CallEvaluation, type ThreadCall } from '../../lib/refonteApi';
import { isConnected } from '../../lib/callMetrics';
import { CLIENT_NOTE_MAX } from '../../lib/clientNote';
import { LIFECYCLE, allowedTransitions, type LifecycleStatus } from '../../lib/appointmentLifecycle';
import StatusBadge from './StatusBadge';
import SourceBadge from './SourceBadge';
import ContractBadge from './ContractBadge';

const gradeTone = (g?: string | null) => {
  const s = (g || '').toUpperCase();
  if (s.startsWith('A')) return 'bg-st-ok/10 text-st-ok';
  if (s.startsWith('B')) return 'bg-st-cy/10 text-st-cy';
  if (s.startsWith('C')) return 'bg-st-warn/10 text-st-warn';
  return 'bg-st-bad/10 text-st-bad';
};

/** Normalise un champ jsonb (array | string JSON | phrase) en liste de puces. */
function toList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return [];
    if (s.startsWith('[')) { try { const p = JSON.parse(s); if (Array.isArray(p)) return p.map(String); } catch { /* ignore */ } }
    return [s];
  }
  return [];
}

/** Bloc « Évaluation de l'appel » (résultat IA de la prise de RDV). Exporté pour un rendu
 *  autonome (ex. carte dédiée en bas de la fiche portail, interne uniquement). */
export const EvalBlock: React.FC<{ ev: CallEvaluation }> = ({ ev }) => {
  const strengths = toList(ev.top_strengths);
  const weaknesses = toList(ev.top_weaknesses);
  return (
    <div className="rounded-lg border border-line bg-panel-2 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-faint">Évaluation de la prise de RDV</span>
        {ev.grade && <span className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded ${gradeTone(ev.grade)}`}>{ev.grade}{ev.score_global != null ? ` · ${Number(ev.score_global).toFixed(1)}/5` : ''}</span>}
      </div>
      {strengths.length ? <div className="mb-1.5"><span className="text-[10px] font-mono text-st-ok">FORCES</span><ul className="text-[12px] text-dim list-disc pl-4">{strengths.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}</ul></div> : null}
      {weaknesses.length ? <div className="mb-1.5"><span className="text-[10px] font-mono text-st-bad">À TRAVAILLER</span><ul className="text-[12px] text-dim list-disc pl-4">{weaknesses.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}</ul></div> : null}
      {ev.action_recommendation && <p className="text-[12px] text-dim mt-1"><span className="text-[10px] font-mono text-faint">RECO · </span>{ev.action_recommendation}</p>}
      {ev.verdict_follow_up && <p className="text-[12px] text-dim mt-1"><span className="text-[10px] font-mono text-faint">SUIVI · </span>{ev.verdict_follow_up}</p>}
    </div>
  );
};

// Motifs (liste fermée en base — CHECK sur not_qualified_reason / dispute_reason). Le texte libre
// était refusé par la contrainte → erreur au moment de valider (retour Vincent 28/09).
const REASONS: Array<[string, string]> = [
  ['hors_icp', 'Hors ICP'],
  ['mauvais_interlocuteur', 'Mauvais interlocuteur'],
  ['pas_de_besoin_exprime', 'Pas de besoin exprimé'],
  ['questions_non_posees', 'Questions de qualif non posées'],
  ['information_erronee', 'Information erronée'],
  ['autre', 'Autre'],
];

const safeHttpUrl = (u?: string | null) => (u && /^https?:\/\//i.test(u) ? u : null);
const fmtDate = (d?: string | null) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—');

/** Message de confirmation (SMS) au PROSPECT, prêt à copier. */
export function confirmationSms(rdv: DayRdv): string {
  const prenom = (rdv.prospect_name || '').split(' ')[0] || '';
  const quand = rdv.time ? `à ${rdv.time.slice(0, 5)}` : '';
  return `Bonjour ${prenom}, je confirme notre rendez-vous du ${rdv.date} ${quand}. `
    + `Au plaisir d'échanger ! N'hésitez pas si vous avez un empêchement.`;
}

/** Récap à envoyer AU CLIENT (repris de l'ancienne plateforme), décliné par statut.
 *  variant 'no_reply' = tentative de confirmation sans réponse (VM/SMS laissé) → ne change PAS le statut. */
export function clientRecapMessage(rdv: DayRdv, variant?: 'no_reply', statusOverride?: LifecycleStatus): string {
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const d = rdv.date ? new Date(rdv.date + 'T00:00:00') : null;
  const dateFr = d ? `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}` : '—';
  const heure = rdv.time ? rdv.time.slice(0, 5) : '—';
  const base = `🏢 Société : ${rdv.company_name || 'N/A'}\n👥 Contact : ${rdv.prospect_name || 'N/A'}\n📞 Téléphone : ${rdv.phone || 'N/A'}`;
  const dt = `📆 Date : ${dateFr}\n⏰ Heure : ${heure}`;
  if (variant === 'no_reply') return `📞 Sans réponse, VM/SMS laissé\n\n${dt}\n${base}`;
  switch (statusOverride ?? rdv.lifecycle_status) {
    case 'confirmed': return `✅ RDV confirmé\n\n${dt}\n${base}`;
    case 'rescheduled': return `📅 RDV reporté\n\n📆 Nouvelle date : ${dateFr}\n⏰ Nouvelle heure : ${heure}\n${base}`;
    case 'held': return `✅ RDV honoré\n\n${dt}\n${base}`;
    case 'no_show': return `❌ No-show\n\n${base}\n📆 Date : ${dateFr} à ${heure}`;
    case 'cancelled': return `❌ RDV annulé\n\n${base}\n📆 Date initiale : ${dateFr} à ${heure}`;
    default: return `📅 RDV planifié\n\n${dt}\n${base}`; // posed / null
  }
}

/** Fiche détail d'un RDV : profil, note, SMS, appel, actions de cycle de vie, report.
 *  Autonome (gère ses propres appels moteur) ; appelle onChanged après une action. */
const RdvDetail: React.FC<{ rdv: DayRdv; role: string; onChanged: () => void; /** conservé pour compat (l'accordéon Conversation s'ouvre par défaut) */ bookingFirst?: boolean; hideEval?: boolean; hideNote?: boolean; hideContract?: boolean; hideClient?: boolean; /** preview interne « vue client » : note client en lecture seule */ previewAsClient?: boolean }> = ({ rdv, role, onChanged, hideEval, hideNote, hideContract, hideClient, previewAsClient }) => {
  const [busy, setBusy] = useState(false);
  // Note du client pour le SDR (champ dédié client_note, écrit via la RPC set_client_note)
  const [cNote, setCNote] = useState(rdv.client_note ?? '');
  const [cNoteBusy, setCNoteBusy] = useState(false);
  const [cNoteSaved, setCNoteSaved] = useState(false);
  const [cNoteErr, setCNoteErr] = useState<string | null>(null);
  useEffect(() => { setCNote(rdv.client_note ?? ''); setCNoteErr(null); }, [rdv.id, rdv.client_note]);
  const [delReqSent, setDelReqSent] = useState(false);
  // Parcours des échanges avec le prospect (tous les appels du même deal/téléphone, chronologie)
  const [thread, setThread] = useState<ThreadCall[]>([]);
  const [threadPlay, setThreadPlay] = useState<{ id: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [clientCopied, setClientCopied] = useState(false);
  const [noReplyCopied, setNoReplyCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const [reschedule, setReschedule] = useState<{ date: string; time: string } | null>(null);
  const [noShowAsk, setNoShowAsk] = useState(false); // no-show → demander la cause (prospect / client)
  const [reasonAsk, setReasonAsk] = useState<LifecycleStatus | null>(null); // not_qualified / disputed → motif (liste fermée)
  const [msgModal, setMsgModal] = useState<string | null>(null); // pop-up « message au client » après un changement de statut
  const [modalCopied, setModalCopied] = useState(false);
  const [editing, setEditing] = useState(false); // mode édition du RDV (correction / réattribution)
  const [ef, setEf] = useState<Record<string, any> | null>(null);
  const [clientsOpt, setClientsOpt] = useState<Array<{ id: string; name: string }>>([]);
  const [sdrsOpt, setSdrsOpt] = useState<Array<{ id: string; name: string }>>([]);
  const [err, setErr] = useState<string | null>(null);
  const [evalRes, setEvalRes] = useState<CallEvaluation | null>(null);
  const [recUrl, setRecUrl] = useState<string | null>(null);

  useEffect(() => {
    setThread([]); setThreadPlay(null);
    getProspectCallThread({
      clientId: (rdv as any).client_id ?? null, phone: rdv.phone ?? null,
      dealId: (rdv as any).pipedrive_deal_id ?? null, callId: rdv.call_id ?? null,
    }).then(t => setThread(
      // Côté client : le parcours ne montre que les vraies conversations (+ l'appel du RDV).
      // Voicemails et appels sans réponse retirés (réunion 15/10 : « les messages vocaux seront retirés »).
      role === 'client' ? t.filter(c => c.isBookingCall || isConnected(c)) : t
    )).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rdv.id, role]);

  useEffect(() => {
    setEvalRes(null); setRecUrl(null);
    if (rdv.call_id) {
      // L'évaluation IA du SDR est une donnée INTERNE : on ne la télécharge même pas côté client
      // (defense in depth — la RLS la bloque aussi depuis le 08/10) (audit UX 09/10 #4).
      if (role !== 'client') getEvaluationByCallId(rdv.call_id).then(setEvalRes).catch(() => {});
      getCallRecordingUrl(rdv.call_id).then(setRecUrl).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rdv.call_id, role]);

  // Statuts qui déclenchent le pop-up « message au client » à copier après le changement.
  const CLIENT_MSG = new Set<LifecycleStatus>(['confirmed', 'held', 'no_show', 'rescheduled', 'posed', 'cancelled']);
  const openMsg = (text: string) => { setModalCopied(false); setMsgModal(text); };

  const act = async (to: LifecycleStatus) => {
    if (to === 'rescheduled') { setReschedule({ date: rdv.date || '', time: (rdv.time || '09:00').slice(0, 5) }); return; }
    if (to === 'no_show') { setNoShowAsk(true); return; } // la cause d'abord (prospect / client)
    if (to === 'not_qualified' || to === 'disputed') { setReasonAsk(to); return; } // motif d'abord (liste fermée)
    setBusy(true); setErr(null);
    try {
      await advanceAppointment(rdv.id, to, { icpConformant: to === 'qualified' ? true : undefined });
      if (CLIENT_MSG.has(to)) openMsg(clientRecapMessage(rdv, undefined, to)); // pop-up à copier
      onChanged();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  // Motif (liste fermée) pour non-qualifié / contestation.
  const doReason = async (code: string) => {
    if (!reasonAsk) return;
    setBusy(true); setErr(null);
    try { await advanceAppointment(rdv.id, reasonAsk, { reason: code }); setReasonAsk(null); onChanged(); }
    catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  // No-show avec sa cause : 'prospect' (pas venu) ou 'client' (a annulé sans replacer).
  const doNoShow = async (cause: 'prospect' | 'client') => {
    setBusy(true); setErr(null);
    try {
      await advanceAppointment(rdv.id, 'no_show', { noShowReason: cause });
      setNoShowAsk(false);
      openMsg(clientRecapMessage(rdv, undefined, 'no_show'));
      onChanged();
    }
    catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  const doReschedule = async () => {
    if (!reschedule) return;
    setBusy(true); setErr(null);
    try {
      await rescheduleAppointment(rdv.id, reschedule.date, reschedule.time);
      // Message au client avec la NOUVELLE date (rdv.date n'est pas encore rafraîchi).
      openMsg(clientRecapMessage({ ...rdv, date: reschedule.date, time: reschedule.time }, undefined, 'rescheduled'));
      setReschedule(null); onChanged();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  const openEdit = async () => {
    setEf({
      prospect_name: rdv.prospect_name || '', company_name: rdv.company_name || '', job_title: rdv.job_title || '',
      phone: rdv.phone || '', email_propsect: (rdv as any).email_propsect || '', prospect_linkedin: rdv.prospect_linkedin || '',
      notes: rdv.notes || (rdv as any).sdr_comments || '', date: rdv.date || '', time: (rdv.time || '').slice(0, 5),
      client_id: (rdv as any).client_id || '', sdr_id: (rdv as any).sdr_id || '',
    });
    setEditing(true); setErr(null);
    if (!clientsOpt.length) getRequestableClients().then(cs => setClientsOpt(cs.map(c => ({ id: c.id, name: c.name })))).catch(() => {});
    // Référentiel profiles (getAgendaSdrs) — la liste dérivée des calls 60 j faisait disparaître
    // Maël et Siril du sélecteur de réassignation (retour Romain 02/10, audit 08/10).
    if (!sdrsOpt.length) getAgendaSdrs().then(ss => setSdrsOpt(ss.map(s => ({ id: s.id, name: s.name })))).catch(() => {});
  };
  const saveEdit = async () => {
    if (!ef) return;
    setBusy(true); setErr(null);
    try {
      await updateAppointmentFields(rdv.id, {
        prospect_name: ef.prospect_name || null, company_name: ef.company_name || null, job_title: ef.job_title || null,
        phone: ef.phone || null, email_propsect: ef.email_propsect || null, prospect_linkedin: ef.prospect_linkedin || null,
        notes: ef.notes || null, date: ef.date || null, time: ef.time || null,
        client_id: ef.client_id || null, sdr_id: ef.sdr_id || null,
      });
      setEditing(false); onChanged();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  const url = safeHttpUrl(rdv.prospect_linkedin);
  const note = rdv.notes || rdv.sdr_comments || rdv.description;

  const isClient = role === 'client';
  const cNoteEditable = isClient && !previewAsClient;
  const cNoteDirty = cNote.trim() !== (rdv.client_note ?? '');
  const cNoteAt = rdv.client_note_at
    ? new Date(rdv.client_note_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;
  const saveClientNote = async () => {
    setCNoteBusy(true); setCNoteErr(null);
    try {
      await setClientNote(rdv.id, cNote);
      setCNoteSaved(true); setTimeout(() => setCNoteSaved(false), 1500);
      onChanged();
    } catch (e: any) { setCNoteErr(e.message); }
    setCNoteBusy(false);
  };
  // Options de changement de statut (dropdown). Hors cycle → « Entrer dans le cycle » (posed).
  const nextOptions: LifecycleStatus[] = rdv.lifecycle_status == null
    ? ['posed']
    : allowedTransitions(rdv.lifecycle_status, role, contractTypeOf(rdv));
  // Rang dans la chaîne pour distinguer une avancée d'un retour arrière (libellé « ↩ Revenir à »).
  const RANK: Partial<Record<LifecycleStatus, number>> = { posed: 0, confirmed: 1, held: 2, qualified: 3, not_qualified: 3, validated: 4, billed: 5 };
  const optionLabel = (o: LifecycleStatus) => {
    if (o === 'posed' && rdv.lifecycle_status == null) return 'Entrer dans le cycle';
    if (o === 'rescheduled') return 'Reporter le RDV';
    const from = rdv.lifecycle_status ?? null;
    const isBack = from != null && RANK[o] != null && RANK[from] != null && (RANK[o] as number) < (RANK[from] as number);
    return isBack ? `↩ Revenir à « ${LIFECYCLE[o].label} »` : LIFECYCLE[o].label;
  };

  // ── Nouvelle architecture (review Charles 09/10 : « trop d'infos partout, répétitions,
  // du blanc partout ») : 3 zones — identité dense / décision en boutons / contexte en accordéons.
  const [openSec, setOpenSec] = useState<string | null>(recUrl || evalRes ? 'conv' : null);
  useEffect(() => { setOpenSec(recUrl || evalRes ? 'conv' : null); }, [rdv.id, !!recUrl, !!evalRes]);
  const Sec: React.FC<{ id: string; label: string; count?: number | null; children: React.ReactNode }> = ({ id, label, count, children }) => (
    <div className="border border-line rounded-lg overflow-hidden">
      <button onClick={() => setOpenSec(o => (o === id ? null : id))}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-panel-2 transition">
        <span className="font-mono text-[10px] text-faint">{openSec === id ? '▾' : '▸'}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-dim">{label}</span>
        {count != null && count > 0 && <span className="font-mono text-[10px] text-faint">({count})</span>}
      </button>
      {openSec === id && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
  // Boutons de décision : les avancées visibles, les retours arrière / cas rares derrière « ⋯ plus ».
  const isBack = (o: LifecycleStatus) => {
    const from = rdv.lifecycle_status;
    return from != null && RANK[o] != null && RANK[from] != null && (RANK[o] as number) < (RANK[from] as number);
  };
  const PRIMARY_ORDER: LifecycleStatus[] = ['confirmed', 'held', 'no_show', 'qualified', 'not_qualified', 'validated', 'billed', 'rescheduled', 'posed'];
  const primary = nextOptions.filter(o => !isBack(o) && o !== 'disputed')
    .sort((a, b) => PRIMARY_ORDER.indexOf(a) - PRIMARY_ORDER.indexOf(b)).slice(0, 4);
  const secondary = nextOptions.filter(o => !primary.includes(o));
  const TONE: Partial<Record<LifecycleStatus, string>> = {
    held: 'border-st-ok/40 text-st-ok hover:bg-st-ok/5',
    qualified: 'border-st-ok/40 text-st-ok hover:bg-st-ok/5',
    validated: 'border-hook-rose/50 text-hook-rose hover:bg-hook-rose/5',
    no_show: 'border-st-bad/40 text-st-bad hover:bg-st-bad/5',
    not_qualified: 'border-st-bad/40 text-st-bad hover:bg-st-bad/5',
  };

  return (
    <div>
      {/* ═ ZONE 1 — IDENTITÉ (dense : qui, quand, badges, contacts — 3 lignes) ═ */}
      <div className="pb-3 mb-3 border-b border-line">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[14px] font-bold capitalize">{fmtDate(rdv.date)}</span>
          <span className="text-[14px] font-semibold text-dim">· {rdv.time?.slice(0, 5) || '—'}</span>
          <span className="ml-auto flex items-center gap-1.5">
            <StatusBadge status={rdv.lifecycle_status ?? null} />
            <SourceBadge source={(rdv as any).source} compact />
            {!isClient && !hideContract && <ContractBadge contractType={contractTypeOf(rdv)} />}
          </span>
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[18px] font-bold leading-tight">{rdv.prospect_name || 'Prospect'}</span>
          <span className="text-[12.5px] text-dim">{rdv.job_title || ''}{rdv.company_name && <> · <b className="text-ink">{rdv.company_name}</b></>}</span>
          {!isClient && (
            <span className="ml-auto font-mono text-[10.5px] text-faint">
              {!hideClient && <>{rdv.clients?.name || '—'} · </>}{rdv.sdr?.sales_name || '—'}
              <button onClick={openEdit} className="ml-2 text-hook-rose hover:underline font-sans text-[11px] font-semibold">✏️ Modifier</button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2.5 flex-wrap mt-1.5 text-[12px]">
          {rdv.phone && (
            <>
              <a href={`tel:${rdv.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 bg-hook-rose text-white font-semibold text-[12.5px] px-2.5 py-1 rounded-lg hover:opacity-90">📞 {rdv.phone}</a>
              <button onClick={() => { navigator.clipboard?.writeText(rdv.phone || ''); setPhoneCopied(true); setTimeout(() => setPhoneCopied(false), 1500); }}
                className="font-mono text-[10.5px] px-1.5 py-0.5 rounded border border-line-2 hover:border-ink">{phoneCopied ? '✓' : 'copier'}</button>
            </>
          )}
          {rdv.email_propsect && <span className="text-dim truncate max-w-[220px]" title={rdv.email_propsect}>✉ {rdv.email_propsect}</span>}
          {url && <a href={url} target="_blank" rel="noreferrer" className="text-hook-rose hover:underline">in LinkedIn ↗</a>}
        </div>
      </div>

      {err && <div className="mb-3 text-[12px] text-st-bad bg-st-bad/10 rounded px-2 py-1">{err}</div>}

      {/* ═ ZONE 2 — DÉCISION : les transitions permises en BOUTONS visibles (plus de dropdown
          caché — les actions existaient mais restaient introuvables, audit UX 09/10). ═ */}
      {(primary.length > 0 || secondary.length > 0) && !editing && (
        <div className="mb-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {primary.map(o => (
              <button key={o} disabled={busy} onClick={() => act(o)}
                className={`text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border transition disabled:opacity-40 ${TONE[o] ?? 'border-line-2 text-ink hover:border-ink'}`}>
                {optionLabel(o)}
              </button>
            ))}
            {secondary.length > 0 && (
              <details className="relative">
                <summary className="list-none cursor-pointer text-[12.5px] px-2.5 py-1.5 rounded-lg border border-line-2 text-dim hover:border-ink select-none">⋯ plus</summary>
                <div className="absolute z-20 mt-1 bg-panel border border-line rounded-lg shadow-xl p-1 min-w-[190px]">
                  {secondary.map(o => (
                    <button key={o} disabled={busy} onClick={() => act(o)}
                      className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md hover:bg-panel-2">{optionLabel(o)}</button>
                  ))}
                  {!isClient && !delReqSent && (
                    <button onClick={async () => {
                      const reason = window.prompt('Pourquoi supprimer ce RDV ? (saisie erronée, doublon…)');
                      if (!reason) return;
                      try { await requestAppointmentDeletion(rdv.id, reason); setDelReqSent(true); }
                      catch (e: any) { setErr(e.message); }
                    }} className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md text-st-bad hover:bg-st-bad/5">⚠ Demander la suppression</button>
                  )}
                </div>
              </details>
            )}
            {delReqSent && <span className="text-[11px] text-st-ok">✓ Demande de suppression envoyée</span>}
          </div>

          {/* Sous-flux inline (cause / motif / report) — s'ouvrent SOUS les boutons, au même endroit */}
          {noShowAsk && (
            <div className="mt-2 border border-st-warn/40 bg-st-warn/5 rounded-lg p-2.5">
              <div className="font-mono text-[9px] uppercase tracking-widest text-st-warn mb-1.5">No-show — à cause de qui ?</div>
              <div className="flex gap-1.5 flex-wrap">
                <button disabled={busy} onClick={() => doNoShow('prospect')} className="text-[12px] font-semibold px-2.5 py-1 rounded-md border border-line-2 hover:border-st-warn">👤 Le prospect (absent)</button>
                <button disabled={busy} onClick={() => doNoShow('client')} className="text-[12px] font-semibold px-2.5 py-1 rounded-md border border-line-2 hover:border-st-warn">🏢 Le client (a annulé)</button>
                <button onClick={() => setNoShowAsk(false)} className="text-[11px] text-faint hover:text-ink px-2">annuler</button>
              </div>
            </div>
          )}
          {reasonAsk && (
            <div className="mt-2 border border-st-warn/40 bg-st-warn/5 rounded-lg p-2.5">
              <div className="font-mono text-[9px] uppercase tracking-widest text-st-warn mb-1.5">{reasonAsk === 'disputed' ? 'Contestation — motif' : 'Non qualifié — motif'}</div>
              <div className="flex gap-1.5 flex-wrap">
                {REASONS.map(([code, lbl]) => (
                  <button key={code} disabled={busy} onClick={() => doReason(code)} className="text-[12px] font-semibold px-2.5 py-1 rounded-md border border-line-2 hover:border-st-warn">{lbl}</button>
                ))}
                <button onClick={() => setReasonAsk(null)} className="text-[11px] text-faint hover:text-ink px-2">annuler</button>
              </div>
            </div>
          )}
          {reschedule && (
            <div className="mt-2 border border-line-2 rounded-lg p-2.5">
              <div className="font-mono text-[9px] uppercase tracking-widest text-dim mb-1.5">Reporter le RDV</div>
              <div className="flex gap-2 flex-wrap items-center">
                <input type="date" value={reschedule.date} onChange={e => setReschedule({ ...reschedule, date: e.target.value })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[12px]" />
                <input type="time" value={reschedule.time} onChange={e => setReschedule({ ...reschedule, time: e.target.value })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[12px]" />
                <button disabled={busy} onClick={doReschedule} className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-hook-rose text-white disabled:opacity-40">Confirmer</button>
                <button onClick={() => setReschedule(null)} className="text-[12px] px-2.5 py-1.5 rounded-md border border-line-2">Annuler</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Note du client pour le SDR — toujours visible (hors accordéon : le SDR doit la voir sans
          cliquer, et une textarea dans <Sec> perdrait le focus à chaque frappe). */}
      {isClient ? (
        <div className="mb-3 rounded-lg border border-line bg-panel-2 p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-dim mb-1.5">✍️ Votre note pour le SDR</div>
          <textarea value={cNote} onChange={e => setCNote(e.target.value)} disabled={!cNoteEditable || cNoteBusy}
            rows={3} maxLength={CLIENT_NOTE_MAX}
            placeholder="Contexte, consignes, retour sur ce RDV… Le SDR la lira avant de rappeler."
            className="w-full rounded-md border border-line-2 bg-panel px-2.5 py-2 text-[12.5px] disabled:opacity-60" />
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            {cNoteEditable && (
              <button onClick={saveClientNote} disabled={cNoteBusy || !cNoteDirty}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-hook-rose text-white disabled:opacity-40">
                {cNoteSaved ? '✓ Enregistrée' : cNoteBusy ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            )}
            {previewAsClient && <span className="text-[11px] text-faint">Aperçu : seul le client peut écrire ici.</span>}
            {cNoteAt && <span className="font-mono text-[10px] text-faint">Modifiée le {cNoteAt}</span>}
            {cNoteErr && <span className="text-[11.5px] text-st-bad">{cNoteErr}</span>}
          </div>
        </div>
      ) : rdv.client_note && (
        <div className="mb-3 rounded-lg border border-hook-rose/40 bg-hook-rose/5 p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-widest text-hook-rose">✍️ Note du client</span>
            {cNoteAt && <span className="font-mono text-[10px] text-faint">{cNoteAt}</span>}
          </div>
          <p className="text-[12.5px] whitespace-pre-wrap max-h-40 overflow-y-auto">{rdv.client_note}</p>
        </div>
      )}

      {/* ═ ZONE 3 — CONTEXTE en accordéons (un seul ouvert : fini les 10 blocs empilés et les
          répétitions — le player du RDV n'existe plus qu'ICI). ═ */}
      <div className="space-y-2">
        {(recUrl || evalRes || rdv.call_id) && !hideEval && (
          <Sec id="conv" label="🎧 Conversation du RDV">
            {recUrl
              ? <audio controls preload="none" src={recUrl} className="w-full h-9 mb-2">Votre navigateur ne peut pas lire l'audio.</audio>
              : rdv.call_id && <p className="text-[11.5px] text-faint mb-2">Enregistrement indisponible pour cet appel.</p>}
            {evalRes && <EvalBlock ev={evalRes} />}
          </Sec>
        )}
        {thread.length > 1 && (
          <Sec id="thread" label="Parcours des échanges" count={thread.length}>
            <div className="divide-y divide-line">
              {thread.map(c => (
                <div key={c.id} className="py-1.5">
                  <div className="flex items-center gap-2 text-[12px] flex-wrap">
                    <span className="font-mono text-faint">{new Date(c.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                    <span className={c.status === 'connected' ? 'text-st-ok' : 'text-faint'}>
                      {c.status === 'connected' ? `${Math.round((c.duration_seconds ?? 0) / 60)} min` : (c.status || '—')}
                    </span>
                    {!isClient && c.sdr_name && <span className="text-dim">{c.sdr_name}</span>}
                    {c.isBookingCall
                      ? <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-hook-rose/10 text-hook-rose" title="C'est l'appel de la section « Conversation du RDV » ci-dessus">appel du RDV ↑</span>
                      : c.status === 'connected' && (
                        <button onClick={async () => {
                          if (threadPlay?.id === c.id) { setThreadPlay(null); return; }
                          const u = await getCallRecordingUrl(c.id).catch(() => null);
                          if (u) setThreadPlay({ id: c.id, url: u });
                        }} className="ml-auto text-[11px] text-hook-rose hover:underline">{threadPlay?.id === c.id ? '■ fermer' : '▶ écouter'}</button>
                      )}
                  </div>
                  {c.call_summary && <p className="text-[11.5px] text-dim mt-0.5 line-clamp-2">{c.call_summary}</p>}
                  {threadPlay?.id === c.id && !c.isBookingCall && <audio controls autoPlay src={threadPlay.url} className="w-full mt-1.5 h-8" />}
                </div>
              ))}
            </div>
          </Sec>
        )}
        {!hideNote && note && (
          <Sec id="note" label="📝 Note du RDV">
            <p className="text-[12.5px] text-dim whitespace-pre-wrap max-h-40 overflow-y-auto">{note}</p>
          </Sec>
        )}
        {!isClient && (
          <Sec id="msg" label="💬 Messages à copier">
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => { navigator.clipboard?.writeText(confirmationSms(rdv)); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                title={confirmationSms(rdv)}
                className="text-[12px] font-semibold px-2.5 py-1.5 rounded-lg border border-line-2 hover:border-ink">{copied ? '✓ Copié' : 'SMS de confirmation au prospect'}</button>
              <button onClick={() => { navigator.clipboard?.writeText(clientRecapMessage(rdv)); setClientCopied(true); setTimeout(() => setClientCopied(false), 1500); }}
                title={clientRecapMessage(rdv)}
                className="text-[12px] font-semibold px-2.5 py-1.5 rounded-lg border border-line-2 hover:border-ink">{clientCopied ? '✓ Copié' : 'Récap au client'}</button>
              <button onClick={() => { navigator.clipboard?.writeText(clientRecapMessage(rdv, 'no_reply')); setNoReplyCopied(true); setTimeout(() => setNoReplyCopied(false), 1500); }}
                title="Message « sans réponse, VM/SMS laissé » — ne change PAS le statut"
                className="text-[12px] font-semibold px-2.5 py-1.5 rounded-lg border border-st-warn/40 text-st-warn hover:bg-st-warn/5">{noReplyCopied ? '✓ Copié' : '📞 Pas de réponse (VM/SMS)'}</button>
            </div>
            <p className="mt-1.5 font-mono text-[9.5px] text-faint">Survole un bouton pour prévisualiser le message. La copie ne change jamais le statut.</p>
          </Sec>
        )}
      </div>

      {/* Édition du RDV : correction du prospect (nom/société/tél/…) + RÉATTRIBUTION client & SDR
          (« attribué à la mauvaise personne »). Ouvert par « Modifier le RDV ». */}
      {editing && ef && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setEditing(false)}>
          <div className="bg-panel border border-line rounded-card p-5 w-full max-w-[460px] max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-hook-rose">Modifier le RDV</span>
              <button onClick={() => setEditing(false)} aria-label="Fermer" className="text-faint hover:text-ink text-[18px] leading-none">×</button>
            </div>
            {err && <div className="mb-3 text-[12px] text-st-bad bg-st-bad/10 rounded px-2 py-1">{err}</div>}
            {(() => {
              const EI = 'mt-0.5 w-full rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]';
              const LB = 'font-mono text-[9px] uppercase tracking-widest text-faint';
              const set = (k: string, v: any) => setEf({ ...ef, [k]: v });
              return (
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="col-span-2 block"><span className={LB}>Prospect</span><input value={ef.prospect_name} onChange={e => set('prospect_name', e.target.value)} className={EI} /></label>
                  <label className="block"><span className={LB}>Société</span><input value={ef.company_name} onChange={e => set('company_name', e.target.value)} className={EI} /></label>
                  <label className="block"><span className={LB}>Poste</span><input value={ef.job_title} onChange={e => set('job_title', e.target.value)} className={EI} /></label>
                  <label className="block"><span className={LB}>Téléphone</span><input value={ef.phone} onChange={e => set('phone', e.target.value)} className={EI} /></label>
                  <label className="block"><span className={LB}>Email</span><input value={ef.email_propsect} onChange={e => set('email_propsect', e.target.value)} className={EI} /></label>
                  <label className="col-span-2 block"><span className={LB}>LinkedIn</span><input value={ef.prospect_linkedin} onChange={e => set('prospect_linkedin', e.target.value)} className={EI} /></label>
                  <label className="block"><span className={LB}>Date</span><input type="date" value={ef.date} onChange={e => set('date', e.target.value)} className={EI} /></label>
                  <label className="block"><span className={LB}>Heure</span><input type="time" value={ef.time} onChange={e => set('time', e.target.value)} className={EI} /></label>
                  <label className="block"><span className={LB}>Client</span>
                    <select value={ef.client_id} onChange={e => set('client_id', e.target.value)} className={EI}>
                      <option value="">— aucun —</option>
                      {clientsOpt.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select></label>
                  <label className="block"><span className={LB}>SDR</span>
                    <select value={ef.sdr_id} onChange={e => set('sdr_id', e.target.value)} className={EI}>
                      <option value="">— aucun —</option>
                      {sdrsOpt.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select></label>
                  <label className="col-span-2 block"><span className={LB}>Note</span><textarea value={ef.notes} onChange={e => set('notes', e.target.value)} rows={3} className={EI} /></label>
                </div>
              );
            })()}
            <div className="flex gap-2 mt-4">
              <button disabled={busy} onClick={saveEdit} className="flex-1 text-[13px] font-semibold px-3 py-2 rounded-lg bg-hook-rose text-white hover:opacity-90 disabled:opacity-50">{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
              <button onClick={() => setEditing(false)} className="text-[13px] px-3 py-2 rounded-lg border border-line-2 hover:border-ink">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Pop-up « message au client » : s'ouvre après un changement de statut (confirmé, reporté,
          honoré, no-show…). Le SDR copie et l'envoie au client. */}
      {msgModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setMsgModal(null)}>
          <div className="bg-panel border border-line rounded-card p-5 w-full max-w-[420px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-hook-rose">Message à envoyer au client</span>
              <button onClick={() => setMsgModal(null)} aria-label="Fermer" className="text-faint hover:text-ink text-[18px] leading-none">×</button>
            </div>
            <p className="text-[12.5px] text-ink bg-panel-2 border border-line rounded-lg p-3 whitespace-pre-wrap max-h-[50vh] overflow-y-auto">{msgModal}</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => { navigator.clipboard?.writeText(msgModal); setModalCopied(true); setTimeout(() => setModalCopied(false), 1500); }}
                className="flex-1 text-[13px] font-semibold px-3 py-2 rounded-lg bg-hook-rose text-white hover:opacity-90">{modalCopied ? '✓ Copié' : 'Copier le message'}</button>
              <button onClick={() => setMsgModal(null)} className="text-[13px] px-3 py-2 rounded-lg border border-line-2 hover:border-ink">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RdvDetail;
